// Libs
import React, { useState, useCallback, useMemo } from 'react';
// Utils
import GENERAL from 'utils/constants/general';
import { debounce, filterOption } from 'utils/libs';
import { getUIValueProp } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';
import OrderAuthorization from 'modules/Orders/Authorization';
// Hooks
import { useOrderServiceAssignment, useTheme } from 'hooks';
import { useSelector } from 'react-redux';
// Components
import { Wrapper, LogoIcon, Select, Span } from 'components';
import Updating from '../Updating';
import Status from '../Status';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Styles
import styles from './AssignedServiceId.module.css';

const { UNSUCCESS } = GENERAL.ENV.STATUS;
const EXISTING_SERVICE_UNAVAILABLE = 'No se pudo consultar el servicio actual';
const UNASSIGNED_SERVICE = 'Sin servicio asignado';
const ENRICHED_MATCH_KEYS = [
	'assigned',
	'status',
	'strategy',
	'candidates',
	'needsReview',
	'needs_review',
];
const componentStyle = {
	select: {
		dark: styles.containerSelectDark,
		light: styles.containerSelectLight,
	},
	dropdown: {
		dark: styles.containerDropdownDark,
		light: styles.containerDropdownLight,
	},
};

const getLabel = value => {
	if (typeof value !== 'string' && typeof value !== 'number') return;
	const label = String(value).trim();
	return label || undefined;
};

const getServiceOptionId = option => {
	const serviceId = Number(
		option?.id ??
			option?.serviceId ??
			option?.service_id ??
			option?.archetypeServiceId,
	);
	return Number.isInteger(serviceId) && serviceId > 0 ? serviceId : undefined;
};

const getResolvedOptionLabel = option => {
	if (!getServiceOptionId(option)) return;
	return getLabel(option?.name ?? option?.serviceName ?? option?.service_name);
};

const getFieldServiceLabel = ({ field, rowData }) => {
	const valueKey = field.uiValueProp ? getUIValueProp(field) : field.name;
	if (!valueKey || valueKey === field.id) return;
	return getLabel(rowData[valueKey]);
};

const hasAssignedServiceId = ({ field, rowData }) => {
	const serviceId = Number(rowData[field.id] ?? rowData.service?.id);
	return Number.isInteger(serviceId) && serviceId > 0;
};

const getServiceMatch = rowData => {
	const diagnostics = rowData.importDiagnostics || {};
	return diagnostics.serviceMatch || diagnostics.service_match || {};
};

const getResolvedServiceLabel = ({ field, rowData }) => {
	if (rowData.exists?.id)
		return (
			getLabel(rowData.exists.service?.name) || EXISTING_SERVICE_UNAVAILABLE
		);

	const match = getServiceMatch(rowData);
	const hasEnrichedMatch = ENRICHED_MATCH_KEYS.some(key =>
		Object.prototype.hasOwnProperty.call(match, key),
	);
	if (hasEnrichedMatch) {
		const assignedLabel = getResolvedOptionLabel(match.assigned);
		return assignedLabel || UNASSIGNED_SERVICE;
	}

	if (!hasAssignedServiceId({ field, rowData })) return UNASSIGNED_SERVICE;
	const serviceLabel = getLabel(rowData.service?.name);
	if (serviceLabel) return serviceLabel;

	return (
		getFieldServiceLabel({ field, rowData }) ||
		getLabel(rowData.serviceName) ||
		UNASSIGNED_SERVICE
	);
};

const getServicesWithCurrentService = (currentService, services) => {
	const currentServiceId = getServiceOptionId(currentService);
	if (
		!Array.isArray(services) ||
		!currentServiceId ||
		!getResolvedOptionLabel(currentService)
	)
		return services;

	const hasCurrentService = services.some(entry => {
		if (!entry?.order_targets || entry.order_targets.length === 0)
			return getServiceOptionId(entry) === currentServiceId;

		return entry.order_targets.some(target =>
			target?.services?.some(
				service => getServiceOptionId(service) === currentServiceId,
			),
		);
	});
	return hasCurrentService ? services : [currentService, ...services];
};

const transformServicesToOptions = data => {
	if (!Array.isArray(data)) return [];

	return data
		.filter(Boolean)
		.flatMap(department => {
			if (!department.order_targets || department.order_targets.length === 0) {
				return [
					{
						label: department.name,
						value: department.id,
					},
				];
			}

			return department.order_targets
				.filter(Boolean)
				.map(ot => {
					if (!ot.services || ot.services.length === 0) return null;

					const serviceOptions = ot.services.filter(Boolean).map(svc => ({
						label: svc.name,
						value: svc.id,
					}));

					return {
						label: `${department.name} / ${ot.name}`,
						options: serviceOptions,
					};
				})
				.filter(Boolean);
		})
		.filter(Boolean);
};

const Selection = ({
	orderServiceAssignment,
	rowData,
	selected,
	field,
	style,
	cleanElementView,
}) => {
	const { themeProfile } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const { state, onGetServiceAssignment, onSetServiceAssignment, onRetry } =
		orderServiceAssignment;

	if (state.status === UNSUCCESS)
		return (
			<Status
				className={componentStyle[style][themeProfile]}
				message={state.message}
				onRetry={onRetry}
			/>
		);

	if (state.isSetting)
		return <Updating className={componentStyle[style][themeProfile]} />;

	const fetchDebouncedServiceAssignment = useCallback(
		debounce(onGetServiceAssignment, 200),
		[],
	);

	const onChange = field.onChange || onSetServiceAssignment;

	const currentService = {
		id: rowData[field.id],
		name: getResolvedServiceLabel({ field, rowData }),
	};

	const _services = !isOpen
		? state.data
		: getServicesWithCurrentService(currentService, state.data);

	const options = useMemo(
		() => transformServicesToOptions(_services),
		[_services],
	);

	return (
		<div
			onClick={e => e.stopPropagation()}
			className={componentStyle[style][themeProfile]}
		>
			<Select
				width='100%'
				showSearch
				filterOption={filterOption}
				placeholder={currentService.name}
				value={
					!_services.length
						? undefined
						: state.selectedServiceId || currentService.id
				}
				onSearch={searchValue =>
					fetchDebouncedServiceAssignment({
						orderTargetFilterId: rowData.orderTargetFilterId,
						searchValue,
					})
				}
				onChange={(_value, { props }) =>
					_value !== currentService.id &&
					onChange({
						field,
						rowData,
						selected,
						option: {
							id: _value,
							name: props.children,
						},
						cleanElementView,
					})
				}
				onDropdownVisibleChange={visible => {
					if (visible) onGetServiceAssignment();
					setIsOpen(visible);
				}}
				dropdownRender={menu => (
					<>
						{menu}
						{state.isLoading && (
							<Wrapper width='100%' justifyContent='center'>
								<LogoIcon spin={true} />
							</Wrapper>
						)}
					</>
				)}
				options={options}
			/>
		</div>
	);
};

const ServiceId = ({ rowData, field, selected, style = 'select' }) => {
	const { isDarkTheme } = useTheme();
	const profile = useSelector(selectProfile);
	const orderServiceAssignment = useOrderServiceAssignment();
	const { onRetry } = orderServiceAssignment;
	const isAuthorized = new OrderAuthorization(profile).checkEditOrderService();
	const isOrderClosed = rowData.eventProps?.freezeEvent;

	if (field.preserveExistingImportService && rowData.exists?.id)
		return (
			<Span title='Esta orden ya existe. Su servicio se conservará y no puede modificarse desde esta importación.'>
				{getResolvedServiceLabel({ field, rowData })}
			</Span>
		);

	if (!isAuthorized || isOrderClosed)
		return <Span>{getResolvedServiceLabel({ field, rowData })}</Span>;

	if (orderServiceAssignment.state.status === UNSUCCESS)
		return (
			<Status
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
				message={orderServiceAssignment.state.message}
				onRetry={onRetry}
			/>
		);

	if (orderServiceAssignment.state.isSetting)
		return (
			<Updating
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
			/>
		);

	return (
		<Selection
			orderServiceAssignment={orderServiceAssignment}
			rowData={rowData}
			selected={selected}
			field={field}
			style={style}
			cleanElementView={rowData.cleanElementView}
		/>
	);
};

export default ServiceId;
